import { useCallback } from 'react';
import { Tag } from '@components/Tag/Tag';
import type { TagProps } from '@components/Tag/Tag';
import type { SelectedOptionsProps } from './types';
import { Icon } from '@components/Icon/Icon';

type OptionRemoveHandler = (
  event: Parameters<TagProps['onDismiss']>[0],
  value: string,
) => void;

/**
 * The container for displaying the selected options and search/filtering
 * input in multi-select.
 *
 * @private
 */
export const SelectedOptions = ({
  selectedOptions,
  onChange,
  isDisabled,
  size = 'm',
}: SelectedOptionsProps): JSX.Element => {
  const onRemove: OptionRemoveHandler = useCallback(
    (event, value): void => {
      if (!onChange) {
        return;
      }

      const newSelectedOptions = selectedOptions.filter(
        (option) => option.value !== value,
      );

      onChange(newSelectedOptions);
    },
    [selectedOptions, onChange],
  );

  const selectedTags = selectedOptions
    ? selectedOptions.map((option) => (
        <Tag
          key={option.value}
          isDismissible={!isDisabled}
          onDismiss={(event) => onRemove(event, option.value)}
          {...(option.icon
            ? { prefixContent: <Icon icon={option.icon} /> }
            : {})}
          size={size}
        >
          {option.label}
        </Tag>
      ))
    : null;

  return <>{selectedTags}</>;
};

SelectedOptions.displayName = 'SelectedOptionsContainer';
